import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import * as Switch from '@radix-ui/react-switch';
import { ChevronDown } from '@/icons';
import { useNotificationPreferencesStore } from '@/hooks/useStores';
import { ActivityDigestCadence } from '@/api/notificationsWrapper';
import '@/styles/form-select.css';
import './notification-preferences-form.css';

interface NotificationPreferencesFormProps {
  formId: string;
  onDirtyChange: (dirty: boolean) => void;
  onSavingChange: (saving: boolean) => void;
  onMessage: (message: { type: 'success' | 'error'; text: string } | null) => void;
}

const NotificationPreferencesForm: React.FC<NotificationPreferencesFormProps> = observer(
  ({ formId, onDirtyChange, onSavingChange, onMessage }) => {
    const { t } = useTranslation('profile');
    const store = useNotificationPreferencesStore();
    const prefs = store.preferences;

    const [digestEnabled, setDigestEnabled] = useState(true);
    const [cadence, setCadence] = useState<string>(ActivityDigestCadence.Weekly);

    const cadenceOptions = useMemo(
      () => [
        { value: ActivityDigestCadence.Off, label: t('notifications.cadence.off') },
        { value: ActivityDigestCadence.Daily, label: t('notifications.cadence.daily') },
        { value: ActivityDigestCadence.Weekly, label: t('notifications.cadence.weekly') },
        { value: ActivityDigestCadence.Monthly, label: t('notifications.cadence.monthly') }
      ],
      [t]
    );

    useEffect(() => {
      void store.loadPreferences();
    }, [store]);

    useEffect(() => {
      if (!prefs) return;
      setDigestEnabled(prefs.activityDigestEnabled && prefs.activityDigest !== ActivityDigestCadence.Off);
      setCadence(
        prefs.activityDigest === ActivityDigestCadence.Off ? ActivityDigestCadence.Weekly : prefs.activityDigest
      );
    }, [prefs]);

    const dirty = useMemo(() => {
      if (!prefs) return false;
      const savedDigestOn = prefs.activityDigestEnabled && prefs.activityDigest !== ActivityDigestCadence.Off;
      const savedCadence =
        prefs.activityDigest === ActivityDigestCadence.Off ? ActivityDigestCadence.Weekly : prefs.activityDigest;
      if (digestEnabled !== savedDigestOn) return true;
      if (digestEnabled && cadence !== savedCadence) return true;
      return false;
    }, [prefs, digestEnabled, cadence]);

    useEffect(() => {
      onDirtyChange(dirty);
    }, [dirty, onDirtyChange]);

    useEffect(() => {
      onSavingChange(store.isSaving);
    }, [store.isSaving, onSavingChange]);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      onMessage(null);
      const ok = await store.updatePreferences({
        activityDigestEnabled: digestEnabled,
        activityDigest: digestEnabled ? (cadence as typeof ActivityDigestCadence.Weekly) : ActivityDigestCadence.Off
      });
      if (ok) {
        onMessage({ type: 'success', text: t('notifications.saved') });
      } else {
        onMessage({ type: 'error', text: store.error ?? t('notifications.saveFailed') });
      }
    };

    if (store.isLoading && !prefs) {
      return <p className="profile-page__hint">{t('notifications.loading')}</p>;
    }

    return (
      <form id={formId} onSubmit={handleSubmit} className="profile-page__form notification-prefs">
        <fieldset className="notification-prefs__fieldset">
          <legend className="notification-prefs__legend">{t('notifications.digestLegend')}</legend>
          <p className="profile-page__hint">{t('notifications.digestHint')}</p>
          <label className="notification-prefs__row">
            <span>{t('notifications.emailDigests')}</span>
            <Switch.Root
              className="notification-prefs__switch"
              checked={digestEnabled}
              onCheckedChange={setDigestEnabled}
              aria-label={t('notifications.digestSwitchAriaLabel')}
            >
              <Switch.Thumb className="notification-prefs__switch-thumb" />
            </Switch.Root>
          </label>
          {digestEnabled && (
            <div className="notification-prefs__row notification-prefs__row--control">
              <span id="digest-cadence-label">{t('notifications.frequency')}</span>
              <Select.Root value={cadence} onValueChange={setCadence}>
                <Select.Trigger
                  className="form-select-trigger notification-prefs__select"
                  aria-labelledby="digest-cadence-label"
                >
                  <Select.Value />
                  <Select.Icon>
                    <ChevronDown width={16} height={16} />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                    {cadenceOptions
                      .filter((o) => o.value !== ActivityDigestCadence.Off)
                      .map((opt) => (
                        <Select.Item key={opt.value} value={opt.value} className="form-select-item">
                          <Select.ItemText>{opt.label}</Select.ItemText>
                        </Select.Item>
                      ))}
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>
          )}
        </fieldset>
      </form>
    );
  }
);

export default NotificationPreferencesForm;
